import type {
    EventDataWinMeterCountUp,
    EventDataWinMeterSetValue,
    EventDataWinMeterShowingFinalValue
} from '@gongxigames/framework/slotV2/types';
import type {StrictEventEmitter} from '@gongxigames/framework/core/services/strictEventEmitter';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals/strictUiEmitter';
import {AutoplayOptions, PartialAutoplayOptions, GameEvents, GenericPopupType, GenericPopupData, ResourcesLoadingEvents} from '@gongxigames/framework/slot';
import {Container} from 'pixi.js';
import {InfoSystemData} from '@gongxigames/framework/slotV2/modules/infoSystem';
import {WaitingEvents} from '@gongxigames/framework/core/globals/strictCoreEmitter/WaitingEvents';
import {ReelsEventMap} from '@gongxigames/framework/slotV2/modules/reelsV2/ReelsV2EventMap';
import {ReelsRespinEventMap} from '@gongxigames/framework/slotV2/modules/reelsRespinV2/ReelsRespinV2EventMap';
import {TumbleReelsEventMap} from '@gongxigames/framework/slotV2/modules/tumbleReels/TumbleReelsEventMap';
import {StrictWinPresentationEvents} from '@gongxigames/framework/slot/events/StrictWinPresentationEvents';
import {Pay} from '@gongxigames/framework/slotV2/types/LogicData';
import {BigWinStrictEvent} from '@gongxigames/framework/slotV2/modules/bigwin/BigWinEvents';

/**
 * Basic game round Spin
 */

export type FeatureEventBuyFeaturePopup = {
    type: 'buyFeaturePopup';
};

export type FeatureEventFreeSpins = {
    type: 'freeSpins';
    amount: number;
};

export type FeatureEventRespins = {
    type: 'respins';
    amount: number;
};

export type FeatureEventRespinsIdle = {
    type: 'respinsIdle';
};

export type FeatureEventPickBonus = {
    type: 'pickBonus';
};

export type FeatureEventSpinGame = {
    type: 'spin';
};

export type FeatureEventGenericInGame = {
    type: 'generic';
};

export type FeatureEventGamePopup = {
    type: 'gamePopup';
    isAutoAcceptable: boolean;
};

export type FeatureEventData =
    FeatureEventBuyFeaturePopup |
    FeatureEventSpinGame |
    FeatureEventFreeSpins |
    FeatureEventRespins |
    FeatureEventRespinsIdle |
    FeatureEventPickBonus |
    FeatureEventGenericInGame;

export type GameOnlyEventMap =
    ReelsEventMap &
    ReelsRespinEventMap &
    TumbleReelsEventMap &
    StrictWinPresentationEvents &
    BigWinStrictEvent &
    GameEvents &
    WaitingEvents &
    ResourcesLoadingEvents &
{
    // Send by game events
    'Game.command.canProceedAutoSpin': void;
    'Game.command.canProceedAutoSpinStop': void;
    'Game.command.round.started': void;
    'Game.command.reels.started': void;
    'Game.command.idle.activated': void;
    'Game.command.round.uiDisabled': symbol | void;
    'Game.command.round.uiEnabled': void;
    'Game.command.round.featureStarted': FeatureEventData;
    'Game.command.round.featureAccepted': void;
    'Game.command.round.featureStopped': FeatureEventData;
    'Game.command.showInfo': InfoSystemData;
    'Game.command.hideInfo': void;
    // TODO: discuss event type
    'Game.command.skipEnable': void;
    'Game.command.skipDisable': void;
    'Game.command.quickStopEnable': void;
    'Game.command.refreshBalance': void;

    'Game.command.WinMeter.COUNT_UP': EventDataWinMeterCountUp;
    'Game.command.WinMeter.STOP_AFTER_SHOWING_FINAL_VALUE': EventDataWinMeterShowingFinalValue;
    'Game.command.WinMeter.STOP_FINAL_VALUE_TIMER': void;
    'Game.command.WinMeter.SET_VALUE': EventDataWinMeterSetValue;
    'Game.command.WinMeter.SNAP_TO_FINAL_VALUE': void;
    'Game.command.WinMeter.STOP': void;

    'General.Game.STARTED': void;
    'Game.command.buyFeature.enable': void;
    'Game.command.buyFeature.disable': void;
    'Game.command.buyFeature.hide': void;
    'Game.command.buyFeature.show': void;
    'Game.command.buyFeature.priceUpdate': void;
    // TODO use widgets API in future
    'Game.command.buyFeature.moveToUi': Container;

    'Game.command.UITicker.show': string;
    'Game.command.UITicker.scatterPay': Pay;
    'Game.command.UITicker.lineWin': [lineNr: string, win: number, multiplier?: number];
    'Game.command.UITicker.waysWin': [waysNr: number, win: number, multiplier?: number];
    'Game.command.UITicker.freeGameBonusWin': void;
    'Game.command.UITicker.setFreeSpinState': boolean;
    'Game.command.UITicker.toggleIdle': boolean;
    'Game.command.UITicker.setIdleLangKeys': string[];
    'Game.command.UITicker.setHighlightColor': number;

    'General.Popup.SHOW': GenericPopupData;
    'General.Flow.SELECT_BONUS_START': void;
    'General.PickBonus.NEW_ROUND': void;
    'General.PickBonus.STARTED': void;
    'General.PickBonus.FINISHED': void;
    'General.PickBonus.PICK_REVEAL': [number, number];
    'General.Game.PLAY_INTRO': void;
    'General.Game.PLAY_ODDS_UPFRONT': void;
    'General.Game.TRANSIT_BACKGROUND': string;
    'General.Game.SYSTEM_INITIALIZED': void;
    'Module.Popup.FADE_OUT_STARTED': Symbol;

    'LogicEvents.PICK_RESPONSE': void;

    // Send by game events

    // Send by UI events
    'UI.command.spin': void;
    'UI.command.skip': void;
    'UI.command.progressBarUpdate': number;
    'UI.command.infoSystem.wait': void;
    'UI.command.infoSystem.show': void;
    'UI.command.menu': void;
    'UI.command.home': void;
    'UI.command.fullScreen': boolean;
    'UI.command.startAutoplay': AutoplayOptions;
    'UI.command.stopAutoplay': void;
    'UI.command.autoplay.changedOption': PartialAutoplayOptions;
    'UI.command.totalBetChanged': void;
    'UI.command.updateSessionBet': void;
    'UI.command.balanceUpdated': number;

    'UI.loading.loaded.defaultPriority': void;
    'UI.loading.loaded.afterPriority': void;

    'Autoplay.STARTED': void;
    'Autoplay.STOPPED': void;
    'General.UI.SPACEBAR_UP': void;
    'General.UI.SPACEBAR_DOWN': void;
    'Generic.POPUP.INTRO_STARTED': GenericPopupType;
    'Generic.POPUP.FADE_OUT_STARTED': GenericPopupType;
    'Generic.POPUP.POPUP_CLOSED': GenericPopupType;
    // Send by UI events
};

// in game it will be named uiEmitter
export const strictGameEmitter = strictUiEmitter as any as StrictEventEmitter<GameOnlyEventMap>;
